// 入力フォームの初期値 (initialValues属性にセットする)
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { useState } from 'react';
import { PagePath } from 'constants/pagePath';
import { SendSMSParam } from 'views/templates/sendSMS';
import { ToastMessageLevel } from 'views/mutual/toast';
import { useSendSmsMutation, useSmsCertificateMutation } from 'views/utils/graphql/graphql';
import { useToast } from 'views/mutual/toast/hooks';
import { authenticationCodeValidation } from 'views/utils/commonValidation';
import { useAuthHandler } from 'views/auth/useAuth';

export const useSendSMS = () => {
  const [SmsCertificatelData, SmsCertificate] = useSmsCertificateMutation();
  const navigate = useNavigate();
  const { toastProps, showToast } = useToast();
  const { authLocationState, onSmsCertificatedSuccess } = useAuthHandler();
  const { provisionalToken: initialProvisionalToken, text } = authLocationState;

  const { onSendSMS, provisionalToken } = useOnRetransmission(initialProvisionalToken, showToast);

  if (!provisionalToken) {
    navigate(PagePath.Login);
  }

  const validate = (values: SendSMSParam) => {
    const errors: Partial<{
      authenticationCode: string;
    }> = {};

    const codeError = authenticationCodeValidation(values.authenticationCode);
    if (codeError) {
      errors.authenticationCode = codeError;
    }

    return errors;
  };

  const onSubmit = useOnceSubmit<SendSMSParam>(
    {
      onSubmit: async (values) => {
        const ret = await SmsCertificate({
          input: {
            Type: 1,
            AuthenticationCode: values.authenticationCode,
            ProvisionalToken: provisionalToken,
          },
        });

        if (ret.data && ret.data.SMSCertificate?.Code !== 0) {
          showToast(ret.data.SMSCertificate?.Message ?? '認証に失敗しました', ToastMessageLevel.Error);
        } else if (ret.data?.SMSCertificate?.Code === 0) {
          onSmsCertificatedSuccess(ret.data.SMSCertificate.Token!);
        }
      },
    },
    [provisionalToken, showToast, onSmsCertificatedSuccess],
  );

  const formik = useFormik<SendSMSParam>({
    initialValues: {
      authenticationCode: '',
    },
    onSubmit,
    validate,
  });

  return { formik, text, onSendSMS, toastProps, isUpdate: false };
};

const useOnRetransmission = (
  initialProvisionalToken: string,
  showToast: (label: string, mesageLevel: ToastMessageLevel) => void,
) => {
  const [provisionalToken, setProvisionalToken] = useState<string>(initialProvisionalToken);
  const [sendSMSData, sendSMS] = useSendSmsMutation();

  const onSendSMS = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const res = await sendSMS({ input: { Type: 1, ProvisionalToken: provisionalToken } });
        if (res.data && res.data.SendSMS?.Code !== 0) {
          showToast(res.data.SendSMS?.Message ?? '認証コードの送信に失敗しました', ToastMessageLevel.Error);
        } else if (res.data?.SendSMS?.Code === 0) {
          setProvisionalToken(res.data.SendSMS.ProvisionalToken!);
          showToast('認証コードを送信しました', ToastMessageLevel.Info);
        }
      },
    },
    [provisionalToken, setProvisionalToken, showToast, sendSMS],
  );

  return {
    onSendSMS,
    provisionalToken,
  };
};
